import { useState } from 'react';
import styled from 'styled-components';
import { useEditMeetingProtocolItemMutation } from '@shared/api';
import {
  PROTOCOL_KINDS,
  protocolKindLabel,
  targetAgentLabel,
  fmtDueDate,
  type MeetingProtocolItem,
} from '@entities/meetings';

const Section = styled.section`
  & + & {
    margin-top: ${({ theme }) => theme.spacing(8)};
  }
`;

const SectionHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(3)};
  margin-bottom: ${({ theme }) => theme.spacing(4)};
  flex-wrap: wrap;
`;

const SectionLabel = styled.div`
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const Actions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing(2)};
`;

const ActionBtn = styled.button<{ $variant?: 'primary' | 'ghost' }>`
  font-size: 13px;
  font-weight: 600;
  padding: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(4)}`};
  border-radius: ${({ theme }) => theme.radius.md};
  cursor: pointer;
  border: 1px solid
    ${({ theme, $variant }) => ($variant === 'ghost' ? theme.colors.border : theme.colors.primary)};
  background: ${({ theme, $variant }) => ($variant === 'ghost' ? 'transparent' : theme.colors.primary)};
  color: ${({ theme, $variant }) => ($variant === 'ghost' ? theme.colors.text.secondary : '#fff')};

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
  &:hover:not(:disabled) {
    filter: brightness(1.06);
  }
`;

const ReviewBanner = styled.div`
  font-size: 13px;
  line-height: 1.5;
  padding: ${({ theme }) => `${theme.spacing(2.5)} ${theme.spacing(3.5)}`};
  margin-bottom: ${({ theme }) => theme.spacing(4)};
  border-radius: ${({ theme }) => theme.radius.md};
  color: ${({ theme }) => theme.colors.text.primary};
  background: ${({ theme }) => `${theme.colors.status.warning}18`};
  border: 1px solid ${({ theme }) => `${theme.colors.status.warning}55`};
`;

const KindBlock = styled.div`
  & + & {
    margin-top: ${({ theme }) => theme.spacing(4)};
  }
`;

const KindTitle = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: ${({ theme }) => theme.spacing(2)};
`;

const ItemRow = styled.div`
  font-size: 14px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.text.primary};
  padding: ${({ theme }) => `${theme.spacing(1)} 0`};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const ItemSub = styled.span`
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const ReviewMark = styled.span`
  color: ${({ theme }) => theme.colors.status.danger};
  font-weight: 600;
`;

const SupersededMark = styled.span`
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const ControlBtn = styled.button`
  margin-left: ${({ theme }) => theme.spacing(2)};
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  padding: ${({ theme }) => `${theme.spacing(0.5)} ${theme.spacing(2)}`};
  border-radius: ${({ theme }) => theme.radius.pill};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: transparent;
  color: ${({ theme }) => theme.colors.text.secondary};

  &:hover {
    border-color: ${({ theme }) => theme.colors.borderStrong};
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

const EditInput = styled.input`
  display: block;
  width: 100%;
  font-size: 13px;
  margin-bottom: ${({ theme }) => theme.spacing(1.5)};
  padding: ${({ theme }) => `${theme.spacing(1.5)} ${theme.spacing(2)}`};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text.primary};

  &:focus-visible {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: ${({ theme }) => theme.shadow.focus};
  }
`;

const EditInlineRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  margin-bottom: ${({ theme }) => theme.spacing(1.5)};
  flex-wrap: wrap;

  & > ${EditInput} {
    flex: 1 1 220px;
    margin-bottom: 0;
  }
`;

const DateInput = styled.input`
  font-size: 12px;
  padding: ${({ theme }) => `${theme.spacing(1)} ${theme.spacing(2)}`};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text.primary};

  &:focus-visible {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: ${({ theme }) => theme.shadow.focus};
  }
`;

const EmptyHint = styled.p`
  margin: 0;
  font-size: 13px;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.text.secondary};
  max-width: 640px;
`;

/** Один пункт протокола: таймкод + текст; для поручения — ответственный/срок;
 *  пометка «(уточнить)» для пунктов, требующих подтверждения (§3.1). Для решений и
 *  поручений — контроль (снять/вернуть) и пометка «снято с контроля» (ТЗ 2.6), под
 *  правом записи. При `editable` (право записи + черновик) — инлайн-правка
 *  текста/ответственного/срока с автодополнением сотрудников и календарём. */
function ProtocolItem({
  item,
  onToggleControl,
  editable,
  meetingId,
}: {
  item: MeetingProtocolItem;
  onToggleControl?: () => void;
  editable?: boolean;
  meetingId?: string;
}) {
  const superseded = item.status !== 'active';
  const isAssignment = item.kind === 'assignment';
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(item.text);
  const [resp, setResp] = useState(item.responsible_name ?? '');
  const [due, setDue] = useState(item.due_text ?? '');
  const [editItem, { isLoading: saving }] = useEditMeetingProtocolItemMutation();

  function cancel() {
    setEditing(false);
    setText(item.text);
    setResp(item.responsible_name ?? '');
    setDue(item.due_text ?? '');
  }

  async function save() {
    if (!text.trim() || !meetingId) return;
    try {
      await editItem({
        itemId: item.id,
        text: text.trim(),
        responsible_name: isAssignment ? resp.trim() || null : undefined,
        due_text: isAssignment ? due.trim() || null : undefined,
        meetingId,
      }).unwrap();
      setEditing(false);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('editMeetingProtocolItem failed', err);
    }
  }

  if (editing) {
    return (
      <ItemRow>
        <EditInput value={text} onChange={(e) => setText(e.target.value)} aria-label="Текст пункта" />
        {isAssignment && (
          <EditInput
            value={resp}
            onChange={(e) => setResp(e.target.value)}
            list="meeting-employees-datalist"
            placeholder="Ответственный — начните вводить ФИО или выберите из списка"
            aria-label="Ответственный"
          />
        )}
        {isAssignment && (
          <EditInlineRow>
            <EditInput
              value={due}
              onChange={(e) => setDue(e.target.value)}
              placeholder="Срок (напр. 10.07.2026 или к пятнице)"
              aria-label="Срок"
            />
            <DateInput
              type="date"
              aria-label="Выбрать дату срока в календаре"
              title="Выбрать точную дату срока"
              onChange={(e) => {
                if (e.target.value) setDue(fmtDueDate(e.target.value));
              }}
            />
          </EditInlineRow>
        )}
        <div>
          <ControlBtn type="button" onClick={() => void save()} disabled={saving || !text.trim()}>
            Сохранить
          </ControlBtn>
          <ControlBtn type="button" onClick={cancel}>
            Отмена
          </ControlBtn>
        </div>
      </ItemRow>
    );
  }

  return (
    <ItemRow>
      {item.timecode ? `[${item.timecode}] ` : ''}
      {item.kind === 'topic_point' && item.speaker ? `${item.speaker}: ` : ''}
      {item.text}
      {item.kind === 'assignment' && (
        <ItemSub>
          {' '}
          — {item.responsible_name ?? 'уточнить'}, срок:{' '}
          {item.due_date ? fmtDueDate(item.due_date) : (item.due_text ?? 'уточнить')}
          {item.speaker ? `, озвучил: ${item.speaker}` : ''}
        </ItemSub>
      )}
      {item.department_name && (item.kind === 'assignment' || item.kind === 'decision') && (
        <ItemSub> · отдел: {item.department_name}</ItemSub>
      )}
      {item.target_agent && (item.kind === 'assignment' || item.kind === 'decision') && (
        <ItemSub> · контур: {targetAgentLabel(item.target_agent)}</ItemSub>
      )}
      {item.needs_review && <ReviewMark> (уточнить)</ReviewMark>}
      {superseded && <SupersededMark> · снято с контроля</SupersededMark>}
      {editable && (
        <ControlBtn type="button" onClick={() => setEditing(true)}>
          Править
        </ControlBtn>
      )}
      {onToggleControl && (
        <ControlBtn type="button" onClick={onToggleControl}>
          {superseded ? 'Вернуть на контроль' : 'Снять с контроля'}
        </ControlBtn>
      )}
    </ItemRow>
  );
}

export interface MeetingProtocolSectionProps {
  meetingId: string;
  protocol?: {
    id: string;
    status: string;
    summary?: string | null;
  } | null;
  items: MeetingProtocolItem[];
  isDraft: boolean;
  canWrite: boolean;
  onApprove: () => void;
  approving: boolean;
  onDecline: () => void;
  declining: boolean;
  onDownloadProtocol: () => void;
  downloadingProtocol: boolean;
  onToggleControl: (item: MeetingProtocolItem) => void;
}

export function MeetingProtocolSection({
  meetingId,
  protocol,
  items,
  isDraft,
  canWrite,
  onApprove,
  approving,
  onDecline,
  declining,
  onDownloadProtocol,
  downloadingProtocol,
  onToggleControl,
}: MeetingProtocolSectionProps) {
  const reviewItemsCount = items.filter(
    (it) =>
      it.needs_review ||
      (it.kind === 'assignment' && (!it.responsible_name?.trim() || !it.due_text?.trim())),
  ).length;

  return (
    <Section>
      <SectionHead>
        <SectionLabel>Протокол</SectionLabel>
        <Actions>
          {protocol && (
            <ActionBtn
              $variant="ghost"
              onClick={onDownloadProtocol}
              disabled={downloadingProtocol}
            >
              Скачать DOCX
            </ActionBtn>
          )}
          {canWrite && isDraft && (
            <>
              <ActionBtn onClick={onApprove} disabled={approving}>
                Утвердить
              </ActionBtn>
              <ActionBtn
                $variant="ghost"
                onClick={onDecline}
                disabled={declining}
              >
                Отклонить
              </ActionBtn>
            </>
          )}
        </Actions>
      </SectionHead>
      {isDraft && reviewItemsCount > 0 && (
        <ReviewBanner>
          Перед утверждением проверьте {reviewItemsCount} п. протокола (отмечены «проверить» или
          содержат поручения без ответственного/срока). Кликните по пункту, чтобы уточнить ФИО
          из справочника и дату исполнения.
        </ReviewBanner>
      )}
      {protocol ? (
        <>
          {protocol.summary && (
            <KindBlock>
              <KindTitle>Краткое содержание</KindTitle>
              <ItemRow>{protocol.summary}</ItemRow>
            </KindBlock>
          )}
          {items.some((it) => it.kind === 'topic') && (
            <KindBlock>
              <KindTitle>Ход обсуждения по темам</KindTitle>
              {items
                .filter((it) => it.kind === 'topic' || it.kind === 'topic_point')
                .map((it) =>
                  it.kind === 'topic' ? (
                    <ItemRow key={it.id}>
                      <strong>
                        {it.timecode ? `[${it.timecode}] ` : ''}
                        {it.text}
                      </strong>
                    </ItemRow>
                  ) : (
                    <ProtocolItem
                      key={it.id}
                      item={it}
                      editable={canWrite && isDraft}
                      meetingId={meetingId}
                    />
                  ),
                )}
            </KindBlock>
          )}
          {PROTOCOL_KINDS.map((kind) => {
            const kindItems = items.filter((it) => it.kind === kind);
            if (kindItems.length === 0) return null;
            return (
              <KindBlock key={kind}>
                <KindTitle>{protocolKindLabel(kind)}</KindTitle>
                {kindItems.map((it) => (
                  <ProtocolItem
                    key={it.id}
                    item={it}
                    editable={canWrite && isDraft}
                    meetingId={meetingId}
                    onToggleControl={
                      canWrite && (kind === 'decision' || kind === 'assignment')
                        ? () => onToggleControl(it)
                        : undefined
                    }
                  />
                ))}
              </KindBlock>
            );
          })}
        </>
      ) : (
        <EmptyHint>Протокол ещё не готов — запись обрабатывается.</EmptyHint>
      )}
    </Section>
  );
}
